import React, { Component, ErrorInfo, ReactNode, Suspense } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import {
  SharedFunctionality,
  useGetSharedFunctionality,
} from './api/lending/lapiHooks';
import { WhiteLabelProvider } from './Buyer/store/WhiteLabelProvider';
import { ImpersonationBanner } from './components/ImpersonationPip/ImpersonationPip';
import { isDemoModeEnabled } from './demo/utils/isDemoModeEnabled';
import { useIntercom } from './hooks/useIntercom';
import { useOnce } from './hooks/useOnce';
import { useQueryParam } from './hooks/useQueryParam';
import { GenericError } from './Pages/FailurePages/GenericError';
import { IwQueryClientProvider } from './store/IwQueryClientProvider';
import { Toaster } from './store/IwToast';
import {
  registerSharedFunctionalities,
  registerStateKey,
} from './utils/fullStory';
import logger from './utils/logger';

const LazyDemoWrapper = React.lazy(() =>
  import('./demo/routing/DemoWrapper').then((module) => ({
    default: module.DemoWrapper,
  })),
);

export const AppWrapper = ({ children }: { children: React.ReactNode }) => {
  const shouldShowDemo = isDemoModeEnabled();

  const BulkComponents = ({ children }: { children: ReactNode }) => {
    return (
      <IwQueryClientProvider>
        <UserIdentifier />
        <IntercomLoader />
        <ReactQueryDevtools initialIsOpen={false} />
        <Toaster />
        <ErrorBoundary>
          <WhiteLabelProvider>{children}</WhiteLabelProvider>
        </ErrorBoundary>
      </IwQueryClientProvider>
    );
  };

  /* if demo, we mock endpoints to provide consistent data for demoing purposes */
  if (shouldShowDemo) {
    return (
      /** By using Suspense + Lazy, MSW code is never imported
       * into the browser (or Vitest) unless this branch is executed.
       */
      <Suspense fallback={null}>
        <LazyDemoWrapper>
          {/* eslint-disable-next-line react-hooks/static-components -- inner component closes over props, extracting changes reconciliation */}
          <BulkComponents>{children}</BulkComponents>
        </LazyDemoWrapper>
      </Suspense>
    );
  }

  return (
    // eslint-disable-next-line react-hooks/static-components -- inner component closes over props, extracting changes reconciliation
    <BulkComponents>
      <ImpersonationBanner />
      {children}
    </BulkComponents>
  );
};

const UserIdentifier = () => {
  const functionalitiesToRegister: SharedFunctionality[] = [
    'use_circe_account_pages',
  ];

  const { stateKey, functionality, loadingFunctionality } =
    useGetSharedFunctionality({
      functionalities: ['use_circe_account_pages'],
    });
  const impersonationStateKey = useQueryParam('state_key');

  useOnce({
    conditionFn: !loadingFunctionality && !!stateKey,
    fn: () => {
      // Register user with Amplitude & FullStory.
      registerStateKey(stateKey!);
      tracking.identifyCustomer({
        stateKey: stateKey!,
        isImpersonating: !!impersonationStateKey,
        country: 'UK',
      });

      if (functionality) {
        // Filter out shared functionalities we do not wish to register with Amplitude & FullStory.
        const functionalities = Object.fromEntries(
          Object.entries(functionality).filter(([name]) =>
            functionalitiesToRegister.includes(name as SharedFunctionality),
          ),
        );

        if (!Object.keys(functionalities).length) return;

        // Register shared functionalities with FullStory & Amplitude.
        registerSharedFunctionalities(stateKey!, functionalities);
        tracking.ampli.identify(stateKey!, {
          // @ts-ignore
          'Split tests': Object.entries(functionalities).map(
            ([name, value]) => `iwocapay_${name}=${value}`,
          ),
        });
      }
    },
  });

  return null;
};

const IntercomLoader = () => {
  useIntercom();
  return null;
};

interface Props {
  children: ReactNode;
  errorComponent: ReactNode;
}

interface State {
  hasError: boolean;
}

export class CustomErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error(error.toString(), { stack: error.stack, ...info });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.errorComponent;
    }

    return this.props.children;
  }
}

export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  // if we are in demo mode we need to show more fail-friendly error messages
  if (localStorage.getItem('demoModeEnabled') === 'true') {
    return (
      <CustomErrorBoundary
        errorComponent={
          <div className="fixed left-0 top-0 flex h-full w-full items-center justify-center">
            <div className="text-center">
              <h2>Coming Soon</h2>
              <p className="mb-0 text-s">
                This part of the experience isn't included in the current demo.
              </p>
              <p className="mt-0 text-s">
                We're working on making it available shortly.
              </p>
            </div>
          </div>
        }
      >
        {children}
      </CustomErrorBoundary>
    );
  }

  return (
    <CustomErrorBoundary
      errorComponent={<GenericError errorText="something went wrong." />}
    >
      {children}
    </CustomErrorBoundary>
  );
};
